import type { TranslateService } from '@ngx-translate/core';
import type { WebWidgetModel, WidgetMetaData } from '@web/types';
import type { PaymentFormWidgetData } from '@web/widgets/payment-form/type/payment-form-widget.type';

import { Widgets } from '@common/enums';
import { defaultWidgetParams, defaultWidgetPermission } from '@common/constants';
import { buttonIconTemplates } from '@common/icons/button-icons';

import { createPaymentFormWidget } from '@sites/entity-core';

import { PaymentFormViewComponent } from '@web/widgets/payment-form/components/payment-form-view/payment-form-view.component';
import { PaymentFormControlComponent } from '@web/widgets/payment-form/components/payment-form-control/payment-form-control.component';

// DEFAULT COMPONENT SETTINGS — sourced from the canonical entity factory in
// @sites/entity-core. The package owns the data shape; host-only concerns —
// the i18n strings and the default button-icon SVG — are passed in via
// `CreateLegacyPaymentFormWidgetOptions`, mirroring the form widget.
//
// `data` is a function so `WebWidgetsFactoryService.prepareWidgetData` invokes
// it with `meta` — a payment form dropped into a zero block starts from the
// zero variant's defaults (`type: 'zero'` envelope tag, extended-position grid).
const paymentFormData = (translateService: TranslateService, meta?: WidgetMetaData): PaymentFormWidgetData => {
    const options = {
        texts: {
            dealName: translateService.instant('crm_deal_name'),
            buttonLabel: translateService.instant('pay'),
            productName: translateService.instant('control_product_name'),
            customPaymentName: translateService.instant('custom_payment_label'),
            promoCodeLabel: translateService.instant('control_promocode_label_placeholder'),
            promoCodePlaceholder: translateService.instant('control_promocode_placeholder_placeholder'),
        },
        buttonIcon: buttonIconTemplates.telegram,
    };

    // The overloads on `createPaymentFormWidget` are picked per literal arg —
    // calling each branch separately keeps overload resolution happy.
    const entity =
        meta?.type === 'zero' ? createPaymentFormWidget('zero', options) : createPaymentFormWidget('default', options);

    return entity.data as unknown as PaymentFormWidgetData;
};
// END DEFAULT SETTINGS

export const paymentFormWidgetModel: WebWidgetModel = {
    componentView: PaymentFormViewComponent,
    componentControl: PaymentFormControlComponent,
    name: Widgets.PAYMENT_FORM,
    label: 'widget_payment_form_label',
    data: paymentFormData,
    permission: defaultWidgetPermission,
    params: { ...defaultWidgetParams, type: ['default', 'specific', 'zero'] },
    // Control + view components own the form via `WebLegacyWidgetSignalFormService`;
    // the resolver takes the minimal load path (just `widget` / `isSpecific` /
    // `isEduSite` — no `widget$`, no `container$`, no `controlForm`).
    signalForm: true,
};
